import { PaymentMethodsEnum, type BillingInfo } from '@ctct/domain-layer';
import type { Payment } from '@ctct/infrastructure-layer';
import type { PaymentMethodsPageStore } from '@ctct/state-client-layer';

import type { AmazonPayService } from './AmazonPayService';
import type { ApplePayService } from './ApplePayService';
import type { PaymentMethodsService } from './PaymentMethodsService';
import type { PaypalService } from './PaypalService';
import type { SecurePayService } from './SecurePayService';

export class InHouseApiPayloadService {
  constructor(
    private pageStore: PaymentMethodsPageStore,
    private billingInfo: BillingInfo,
    private paymentMethodsService: PaymentMethodsService,
    private securePayService: SecurePayService,
    private amazonPayService: AmazonPayService,
    private paypalService: PaypalService,
    private applePayService: ApplePayService
  ) {}

  public api() {
    return {
      /**
       * validates data and creates payload
       */
      getPayloadForUpdateBillingInfoApi: async () => {
        const selectedPaymentType = this.paymentMethodsService.data().getSelectedMethod();

        const base = {
          payment_type: selectedPaymentType.toLowerCase(),
          credit_card_info: null,
          echeck_info: null,
          check: null,
          paypal: null,
          amazonpay: null,
        };

        // apple pay
        if (this.paymentMethodsService.data().isApplePay()) {
          const applePayCreditCardData = this.applePayService.data().getApplePayCreditCardData();

          return {
            ...base,
            payment_type: 'creditcard',
            credit_card_info: applePayCreditCardData,
          };
        }

        // credit card
        if (this.paymentMethodsService.data().isCreditCard()) {
          const credit_card_info = await this.securePayService.handlers().validateCreditCard();

          return {
            ...base,
            credit_card_info,
          };
        }

        // echeck
        if (this.paymentMethodsService.data().isEcheck()) {
          const echeck_info = await this.securePayService.handlers().validateEcheck();

          return {
            ...base,
            echeck_info,
          };
        }

        // amazon pay
        if (this.paymentMethodsService.data().isAmazonPay()) {
          const amazonpay = this.amazonPayService.handlers().validateAmazonPay();

          return {
            ...base,
            amazonpay,
          };
        }

        // pay pal
        if (this.paymentMethodsService.data().isPayPal()) {
          const paypal = this.paypalService.handlers().validatePaypal();

          return {
            ...base,
            paypal,
          };
        }

        throw new Error('Unknown payment method');
      },

      getPayloadForCheckoutApi: async () => {
        return this.getCheckoutPayload();
      },

      // for overdue payment endpoint we need to have almost the same payload as for checkout,
      // but with minor difference for paypal; we too send "paypal" instead of "payPal"
      getPayloadForOverduePayment: async () => {
        const payload = await this.getCheckoutPayload();

        const { payPal, ...rest } = payload;

        return {
          ...rest,
          paypal: payPal,
        };
      },
    };
  }

  private async getCheckoutPayload(): Promise<Payment> {
    const selectedPaymentType = this.paymentMethodsService.data().getSelectedMethod();

    const base: Payment = {
      paymentType: {
        name:
          selectedPaymentType === PaymentMethodsEnum.CREDIT_CARD ? 'CREDIT CARD' : selectedPaymentType.toUpperCase(),
        displayString:
          selectedPaymentType === PaymentMethodsEnum.AMAZON_PAY ? 'Amazon Pay' : selectedPaymentType.toLowerCase(),
      },
      creditCard: null,
      check: null,
      echeck: null,
      payPal: null,
      amazonPay: null,
      origin: null,
      commitDirective: null,
      stripe: null,
    };

    // apple pay
    if (this.paymentMethodsService.data().isApplePay()) {
      const applePayToken = this.applePayService.data().getApplePayToken();

      return {
        ...base,
        paymentType: {
          // apple pay is treated as a credit card
          name: 'CREDIT CARD',
          displayString: 'creditcard',
        },
        creditCard: {
          cardNumber: null,
          cardExpirationDate: null,
          cardSecureCode: null,
          cardHolderName: '',
          validateCardSecureCode: false,
          cardToken: applePayToken,
          data: null,
          expired: false,
          tokenized: true,
          digitalWallet: PaymentMethodsEnum.APPLE_PAY,
        },
      };
    }

    // credit card
    if (this.paymentMethodsService.data().isCreditCard()) {
      const { securePayEdited } = this.pageStore.getState();
      const shouldValidateCreditCard =
        securePayEdited ||
        !this.billingInfo.getCreditCardInfo() ||
        // if current payment method is apple pay, and we want to update it to credit card, we need to validate credit card
        this.billingInfo.getIsCreditCardFromApplePayWallet();

      const credit_card_info = shouldValidateCreditCard
        ? await this.securePayService.handlers().validateCreditCard()
        : this.billingInfo.getCreditCardInfo();

      if (!credit_card_info) {
        throw new Error('Could not get `credit_card_info`');
      }

      return {
        ...base,
        creditCard: {
          cardNumber: null,
          cardExpirationDate: null,
          cardSecureCode: null,
          cardHolderName: '',
          validateCardSecureCode: false,
          cardToken: credit_card_info?.card_token,
          data: null,
          expired: false,
          tokenized: true,
          digitalWallet: null,
        },
      };
    }

    // echeck
    if (this.paymentMethodsService.data().isEcheck()) {
      const { securePayEdited } = this.pageStore.getState();
      const shouldValidateEcheck = securePayEdited || !this.billingInfo.getEcheckInfo();

      const echeck_info = shouldValidateEcheck
        ? await this.securePayService.handlers().validateEcheck()
        : this.billingInfo.getEcheckInfo();

      if (!echeck_info) {
        throw new Error();
      }

      return {
        ...base,
        echeck: {
          accountType: {
            code: echeck_info.account_type?.toUpperCase() === 'CHECKING' ? 1 : 2,
            checking: echeck_info.account_type?.toUpperCase() === 'CHECKING',
            savings: echeck_info.account_type?.toUpperCase() === 'SAVINGS',
            corporate: false,
            corporateSavings: false,
            displayString: echeck_info.account_type?.toUpperCase(),
          },
          routingNumber: echeck_info.routing_number,
          accountNumber: echeck_info.account_number,
          token: echeck_info.echeck_token,
        },
      };
    }

    // amazon pay
    if (this.paymentMethodsService.data().isAmazonPay()) {
      const amazonpay = this.amazonPayService.handlers().validateAmazonPay();

      return {
        ...base,
        amazonPay: { token: amazonpay.token },
      };
    }

    // pay pal
    if (this.paymentMethodsService.data().isPayPal()) {
      const paypal = this.paypalService.handlers().validatePaypal();

      return {
        ...base,
        payPal: { paypalAgreementId: paypal.agreement_id },
      };
    }

    throw new Error('Unknown payment method');
  }
}
